import {
  CHANCE_PACKS,
  type InventoryDto,
  WHEEL_SEGMENTS,
  WHEEL_SPIN_MS,
  type WheelDto,
  wheelOdds,
  type WheelSpinDto,
} from '@spin/shared';
import { useEffect, useRef, useState } from 'react';
import { clientId, http } from '../api/http';
import { Emoji, emojiSrc } from '../components/Emoji';
import { CloseIcon, GmCoin } from '../components/icons';
import { ChanceArt } from '../components/PackArt';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { useUi, wheelSpin } from '../store/ui';
import { tg } from '../tg/bridge';

const SLICE = 360 / WHEEL_SEGMENTS.length;
/** The arrow stands at 9 o'clock: the segment under it is the prize. */
const POINTER_ANGLE = 270;

/** How each prize is drawn on the wheel. */
const LOOK: Record<string, { icon: string; label: string }> = {
  hearts_3: { icon: '❤️', label: '3' },
  hearts_5: { icon: '❤️', label: '5' },
  hearts_25: { icon: '❤️', label: '25' },
  hearts_100: { icon: '❤️', label: '100' },
  jackpot: { icon: '❤️', label: '1000' },
  chance_1: { icon: '🎲', label: '1' },
  chance_3: { icon: '🎲', label: '3' },
  boost_x2: { icon: '⭐', label: '×2' },
  boost_limit: { icon: '⭐', label: '+10' },
  boost_bonus: { icon: '⭐', label: '+5' },
};

/** Point at `angle` degrees clockwise from the top, `r` from the centre. */
function xy(angle: number, r: number): [number, number] {
  const a = (angle * Math.PI) / 180;
  return [+(r * Math.sin(a)).toFixed(2), +(-r * Math.cos(a)).toFixed(2)];
}
const point = (angle: number, r: number) => xy(angle, r).join(' ');

/** A green die, the "chance". */
export function Die({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 20 20" width={size} height={size} aria-hidden className="die">
      <rect x="1" y="1" width="18" height="18" rx="4" fill="#1fbf8f" stroke="#12906a" strokeWidth="1.2" />
      {[
        [6, 6],
        [14, 6],
        [10, 10],
        [6, 14],
        [14, 14],
      ].map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.7" fill="#0b4d3a" />
      ))}
    </svg>
  );
}

function WheelArt({ rotation, spinning }: { rotation: number; spinning: boolean }) {
  return (
    <svg className="wheel-svg" viewBox="-104 -104 208 208" aria-hidden>
      <defs>
        <linearGradient id="w-rim" x1="0" y1="-1" x2="0" y2="1">
          <stop offset="0" stopColor="#ffe27a" />
          <stop offset="0.5" stopColor="#f6b91c" />
          <stop offset="1" stopColor="#e39a0c" />
        </linearGradient>
        <radialGradient id="w-hub">
          <stop offset="0" stopColor="#fffdf2" />
          <stop offset="1" stopColor="#f3e3b0" />
        </radialGradient>
        <linearGradient id="w-heart" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffe9a3" />
          <stop offset="1" stopColor="#f1b21e" />
        </linearGradient>
      </defs>
      <circle r="103" fill="url(#w-rim)" stroke="#d58a06" strokeWidth="2" />
      <circle r="88" fill="#109e7c" />
      <g
        className="wheel-rotor"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: spinning ? `transform ${WHEEL_SPIN_MS}ms cubic-bezier(0.15, 0.6, 0.1, 1)` : 'none',
        }}
      >
        {WHEEL_SEGMENTS.map((code, i) => {
          const look = LOOK[code]!;
          const a = i * SLICE;
          return (
            <g key={i}>
              <path
                d={`M 0 0 L ${point(a - SLICE / 2, 87)} A 87 87 0 0 1 ${point(a + SLICE / 2, 87)} Z`}
                fill={i % 2 ? '#14b38d' : '#1cc9a0'}
                stroke="#7fe8cf"
                strokeWidth="0.8"
              />
              <g transform={`rotate(${a})`}>
                <SegmentIcon emoji={look.icon} />
                <text y="-47" textAnchor="middle" className="wheel-label" transform="rotate(90 0 -47)">
                  {look.label}
                </text>
              </g>
            </g>
          );
        })}
      </g>
      <circle r="21" fill="url(#w-hub)" stroke="#e8b33a" strokeWidth="2.5" />
      <path
        d="M0 11C-6 6.5-12 2.8-12-3.3c0-3.6 2.7-6.2 6-6.2 2.6 0 4.8 1.5 6 3.8 1.2-2.3 3.4-3.8 6-3.8 3.3 0 6 2.6 6 6.2 0 6.1-6 9.8-12 14.3Z"
        fill="url(#w-heart)"
        stroke="#d99a12"
        strokeWidth="1"
      />
    </svg>
  );
}

function percent(p: number): string {
  return `${p >= 1 ? p.toFixed(0) : p >= 0.1 ? p.toFixed(1) : p.toFixed(2)}%`;
}

/** "Shop of Chances": chance packs for GM. */
function ChancesShop({
  gm,
  onBought,
  onClose,
}: {
  gm: number;
  onBought: (res: { inventory: InventoryDto; gm: number }) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const buy = async (pack: (typeof CHANCE_PACKS)[number]) => {
    if (pack.gm > gm) {
      useUi.getState().toast(t('errors.INSUFFICIENT_GM'), 'error');
      return;
    }
    setBusy(true);
    try {
      const res = await http.post<{ balance: number; gm: number; inventory: InventoryDto }>(
        '/wheel/chances',
        {
          pack: pack.code,
          clientTxId: clientId(),
        },
      );
      useSession.getState().setWallet({ balance: res.balance, gm: res.gm });
      onBought(res);
      tg.haptic('success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="chances-layer" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="chances-shop" role="dialog" data-testid="chances-shop">
        <button className="dialog-close" onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon />
        </button>
        <div className="chances-title">{t('wheel.shop')}</div>
        <div className="chances-grid">
          {CHANCE_PACKS.map((p) => (
            <button
              key={p.code}
              className="chance-tile"
              disabled={busy}
              onClick={() => void buy(p)}
              data-testid={`buy-${p.code}`}
            >
              <b>{p.chances}</b>
              <span className="chance-art" aria-hidden>
                <ChanceArt chances={p.chances} />
              </span>
              <span className="gm-price">
                <GmCoin size={15} />
                {p.gm}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Wheel of Fortune over the game: the rules and chances on a dark card, the wheel with the arrow on the left, "Spin"
 * for one chance. Chances are bought for GM in the Shop of Chances; the server draws the prize.
 */
export function WheelOverlay() {
  const t = useT();
  const close = useUi.getState().closeDialog;
  const [state, setState] = useState<WheelDto | null>(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shop, setShop] = useState(false);
  const [help, setHelp] = useState(false);
  const pending = useRef<{ timer: number; apply: () => void } | null>(null);

  useEffect(() => tg.backButton.show(close), [close]);
  useEffect(() => {
    http.get<WheelDto>('/wheel').then(setState).catch(reportError);
    // Leaving mid-spin still applies the prize to the balance shown elsewhere.
    return () => {
      if (pending.current) {
        clearTimeout(pending.current.timer);
        pending.current.apply();
      }
    };
  }, []);

  const spin = async () => {
    if (!state || spinning) return;
    if (state.inventory.chances < 1) {
      setShop(true);
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await http.post<WheelSpinDto>('/wheel/spin', { clientTxId: clientId() });
      tg.haptic('medium');
      setState({ ...state, inventory: { ...state.inventory, chances: state.inventory.chances - 1 } });
      const want = (((POINTER_ANGLE - res.segment * SLICE) % 360) + 360) % 360;
      const current = ((rotation % 360) + 360) % 360;
      const jitter = (Math.random() - 0.5) * SLICE * 0.6;
      setSpinning(true);
      setRotation(rotation + 360 * 6 + ((want - current + 360) % 360) + jitter);
      wheelSpin.until = Date.now() + WHEEL_SPIN_MS + 500;
      const apply = () => {
        pending.current = null;
        useSession.getState().setBalance(res.balance);
        setState((s) => (s ? { ...s, inventory: res.inventory } : s));
      };
      pending.current = {
        timer: window.setTimeout(() => {
          apply();
          setSpinning(false);
          setResult(res.prize);
          tg.haptic('success');
        }, WHEEL_SPIN_MS),
        apply,
      };
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  const chances = state?.inventory.chances ?? 0;
  return (
    <div className="wheel-layer" data-testid="wheel">
      <button
        className="wheel-close"
        onClick={close}
        aria-label={t('common.close')}
        data-testid="dialog-close"
      >
        <CloseIcon />
      </button>
      <div className="wheel-info">
        <button className="wheel-help" onClick={() => setHelp((h) => !h)} aria-label={t('wheel.help')}>
          ?
        </button>
        <b className="wheel-title">{t('wheel.title')}</b>
        {help ? (
          <div className="wheel-odds">
            <p>{t('wheel.help_text')}</p>
            {wheelOdds().map((o) => (
              <div key={o.code} className="odds-row">
                <span>
                  <Emoji e={LOOK[o.code]?.icon ?? ''} /> {t(`wheel.prize.${o.code}`)}
                </span>
                <b>{percent(o.percent)}</b>
              </div>
            ))}
          </div>
        ) : (
          <p className="wheel-rules">{t('wheel.rules')}</p>
        )}
        <div className="wheel-chances">
          {t('wheel.chances_label')}
          <span className="chance-pill" data-testid="chances">
            <Die />
            {chances}
            <button onClick={() => setShop(true)} aria-label={t('wheel.shop')} data-testid="open-chances">
              +
            </button>
          </span>
        </div>
      </div>
      <div className="wheel-stage">
        <WheelArt rotation={rotation} spinning={spinning} />
        <svg className="wheel-pointer" viewBox="0 0 60 44" aria-hidden>
          <path d="M4 22 18 6h20l18 16-18 16H18Z" fill="#f6b91c" stroke="#c98500" strokeWidth="2.5" />
          <path d="M14 22h30M36 14l8 8-8 8" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
        </svg>
        {result && (
          <div className="wheel-won" data-testid="wheel-result" data-prize={result}>
            {t('wheel.won', { prize: t(`wheel.prize.${result}`) })}
          </div>
        )}
      </div>
      <div className="wheel-price">
        {t('wheel.price')} <Die /> <b>1</b>
      </div>
      <button
        className="btn btn-green wheel-spin"
        disabled={busy || spinning || !state}
        onClick={() => void spin()}
        data-testid="wheel-spin"
      >
        {t('wheel.spin')}
      </button>
      {shop && state && (
        <ChancesShop
          gm={state.gm}
          onBought={(res) => setState({ ...state, gm: res.gm, inventory: res.inventory })}
          onClose={() => setShop(false)}
        />
      )}
    </div>
  );
}

/** A prize icon on a wheel segment: the 3D picture, or the emoji as text when there is none. */
function SegmentIcon({ emoji }: { emoji: string }) {
  const src = emojiSrc(emoji);
  if (!src) {
    return (
      <text y="-66" textAnchor="middle" fontSize="15">
        {emoji}
      </text>
    );
  }
  return (
    <image
      href={src}
      x="-9.5"
      y="-80"
      width="19"
      height="19"
      className={emoji === '🎲' ? 'wheel-die' : undefined}
    />
  );
}
